import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Paper, Grid, Card, CardContent,
  Table, TableHead, TableRow, TableCell, TableBody,
  FormControl, InputLabel, Select, MenuItem, Snackbar, Alert,
  Chip, LinearProgress, Tabs, Tab
} from '@mui/material';
import {
  Refresh as RefreshIcon, Search as SearchIcon, 
  Assessment as AssessmentIcon, Timeline as TimelineIcon,
  Report as ReportIcon
} from '@mui/icons-material';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';

const FacilityUsageReports = () => {
  const [usageLogs, setUsageLogs] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [filters, setFilters] = useState({
    facilityId: '',
    employeeId: '',
    startDate: '',
    endDate: '',
    status: ''
  });
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const statusColors = {
    'Approved': '#4caf50',
    'Rejected': '#f44336',
    'Charged': '#ff9800',
    'Exceeded': '#9c27b0'
  };

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const fetchFacilities = useCallback(async () => {
    try {
      const response = await axios.get('/facilities');
      setFacilities(response.data.facilities || []);
    } catch (error) {
      console.error('Error fetching facilities:', error);
    }
  }, []);

  const fetchUsageLogs = useCallback(async () => {
    setLoading(true);
    try {
      // Mock data for now since endpoint doesn't exist yet
      const mockLogs = [];
      setUsageLogs(mockLogs);
    } catch (error) {
      console.error('Error fetching usage logs:', error);
      showSnackbar('Kullanım logları yüklenirken hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, showSnackbar]);

  useEffect(() => {
    fetchFacilities();
    fetchUsageLogs();
  }, [fetchUsageLogs, fetchFacilities]);

  const fetchSummaryData = async () => {
    try {
      // This endpoint would need to be created in the backend
      // const response = await axios.get('/facility-usage/summary');
      // setSummaryData(response.data || {});
      console.log('Summary data endpoint not implemented yet');
    } catch (error) {
      console.error('Error fetching summary data:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    fetchUsageLogs();
  };

  const handleRefresh = () => {
    fetchUsageLogs();
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const getStatusColor = (status) => {
    const colors = {
      'Approved': 'success',
      'Rejected': 'error',
      'Charged': 'warning',
      'Exceeded': 'secondary'
    };
    return colors[status] || 'default';
  };

  const prepareChartData = () => {
    // Prepare data for charts
    const dailyUsage = usageLogs.reduce((acc, log) => {
      const date = new Date(log.UsageDateTime).toLocaleDateString('tr-TR');
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    const chartData = Object.entries(dailyUsage).map(([date, count]) => ({
      date,
      count
    }));

    return chartData;
  };

  const prepareFacilityUsageData = () => {
    const facilityUsage = usageLogs.reduce((acc, log) => {
      const facilityName = log.FacilityName || 'Bilinmeyen';
      acc[facilityName] = (acc[facilityName] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(facilityUsage).map(([name, count]) => ({
      name,
      count
    }));
  };

  const prepareStatusData = () => {
    const statusCount = usageLogs.reduce((acc, log) => {
      acc[log.UsageStatus] = (acc[log.UsageStatus] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(statusCount).map(([status, count]) => ({
      status,
      count,
      color: statusColors[status] || '#cccccc'
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Tesis Kullanım Raporları</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
        >
          Yenile
        </Button>
      </Box>

      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Özet Dashboard" icon={<AssessmentIcon />} />
        <Tab label="Kullanım Logları" icon={<TimelineIcon />} />
        <Tab label="Grafikler" icon={<ReportIcon />} />
      </Tabs>

      {/* Summary Dashboard Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Toplam Kullanım</Typography>
                <Typography variant="h4" color="primary">
                  {usageLogs.length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Son 30 gün
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Onaylanan</Typography>
                <Typography variant="h4" color="success.main">
                  {usageLogs.filter(log => log.UsageStatus === 'Approved').length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Başarılı girişler
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Reddedilen</Typography>
                <Typography variant="h4" color="error.main">
                  {usageLogs.filter(log => log.UsageStatus === 'Rejected').length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Başarısız denemeler
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Ücretli</Typography>
                <Typography variant="h4" color="warning.main">
                  {usageLogs.filter(log => log.UsageStatus === 'Charged').length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Ücretlendirilen kullanımlar
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Facility Usage Summary */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Tesise Göre Kullanım</Typography>
                <Box sx={{ height: 300 }}>
                  <BarChart data={prepareFacilityUsageData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#1976d2" />
                  </BarChart>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Durum Dağılımı</Typography>
                <Box sx={{ height: 300 }}>
                  <PieChart>
                    <Pie
                      data={prepareStatusData()}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="count"
                      label
                    >
                      {prepareStatusData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Usage Logs Tab */}
      {activeTab === 1 && (
        <Box>
          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Filtreler</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tesis</InputLabel>
                  <Select
                    value={filters.facilityId}
                    onChange={(e) => handleFilterChange('facilityId', e.target.value)}
                  >
                    <MenuItem value="">Tümü</MenuItem>
                    {facilities.map((facility) => (
                      <MenuItem key={facility.ID} value={facility.ID}>
                        {facility.FacilityName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Personel ID"
                  value={filters.employeeId}
                  onChange={(e) => handleFilterChange('employeeId', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Başlangıç"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Bitiş"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Durum</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <MenuItem value="">Tümü</MenuItem>
                    <MenuItem value="Approved">Onaylandı</MenuItem>
                    <MenuItem value="Rejected">Reddedildi</MenuItem>
                    <MenuItem value="Charged">Ücretli</MenuItem>
                    <MenuItem value="Exceeded">Aşıldı</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  disabled={loading}
                >
                  Ara
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Usage Logs Table */}
          {loading && <LinearProgress sx={{ mb: 2 }} />}
          
          <Paper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tarih</TableCell>
                  <TableCell>Personel ID</TableCell>
                  <TableCell>Kart UID</TableCell>
                  <TableCell>Tesis</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>Red Nedeni</TableCell>
                  <TableCell>Ücret</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usageLogs.map((log) => (
                  <TableRow key={log.ID}>
                    <TableCell>
                      {new Date(log.UsageDateTime).toLocaleString('tr-TR')}
                    </TableCell>
                    <TableCell>{log.EmployeeId}</TableCell>
                    <TableCell>{log.CardUID}</TableCell>
                    <TableCell>{log.FacilityName || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={log.UsageStatus} 
                        color={getStatusColor(log.UsageStatus)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{log.RejectReason || '-'}</TableCell>
                    <TableCell>
                      {log.ChargedAmount ? `${log.ChargedAmount} ${log.Currency || 'TL'}` : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                {usageLogs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="textSecondary">
                        Kullanım logu bulunmamaktadır
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}

      {/* Charts Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Günlük Kullanım Trendi</Typography>
                <Box sx={{ height: 400 }}>
                  <LineChart data={prepareChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="count" stroke="#1976d2" />
                  </LineChart>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FacilityUsageReports;
